/**
 * Build a products-query string from SKUs.
 * Prefer `products` over Storefront `search` — longer alphanumeric SKUs
 * (e.g. Z04816RAVBISCHO) are often missing from search results.
 * @param { Array<string> } skus
 * @returns { { query: string, first: number } }
 */
const buildSkuQueryVariables = (skus) => {
  const cleanSkus = skus.map((sku) => (typeof sku === 'string' ? sku.trim() : '')).filter(Boolean)

  return {
    query: cleanSkus.join(' OR '),
    first: Math.min(Math.max(cleanSkus.length * 2, 10), 50)
  }
}

/**
 * Used to generate swatches
 * @param { Array<string> } skus
 * @returns { { query: string, variables: { query: string, first: number } } }
 */
export const queryGetManyProductsBySKU = (skus) => {
  if (!skus || !(skus instanceof Array)) {
    throw new Error(`Must provide skus to queryGetManyProductsBySKU; provided: ${typeof skus === 'object' ? JSON.stringify(skus) : skus} `)
  }

  const variables = buildSkuQueryVariables(skus)
  const query = `
    query productsBySku($query: String!, $first: Int) {
      products(first: $first, query: $query) {
        edges {
          node {
            id
            title
            handle
            totalInventory
            featuredImage {
              url
              altText
            }
            images(first: 10) {
              nodes {
                url
                altText
              }
            }
            priceRange {
              minVariantPrice {
                amount
                currencyCode
              }
            }
            compareAtPriceRange {
              minVariantPrice {
                amount
                currencyCode
              }
            }
            tags
            options(first: 5) {
              name
            }
            variants(first: 5) {
              edges {
                node {
                  id
                  availableForSale
                  currentlyNotInStock
                  quantityAvailable
                  title
                  selectedOptions {
                    value
                  }
                  sku
                  earliestPoDate: metafield(namespace: "custom", key: "earliestpodate") {
                    value
                  }
                  onHandStock: metafield(namespace: "custom", key: "onhandstock") {
                    value
                  }
                  futureStock: metafield(namespace: "custom", key: "futurestock") {
                    value
                  }
                  brisbaneStockJson: metafield(namespace: "custom", key: "brisbane-stock-json") {
                    value
                  }
                  melbourneStockJson: metafield(namespace: "custom", key: "melbourne-stock-json") {
                    value
                  }
                }
              }
            }
            prettyColorName: metafield(namespace: "variant_options", key: "colourway") {
              value
            }
            bundleChilds: metafield(namespace: "linked_products", key: "package_items") {
              value
            }
            colorName: metafield(namespace: "variant_options", key: "colour_swatch") {
              value
            }
            video: metafield(namespace: "custom", key: "product_video") {
              value
            }
            size: metafield(namespace: "variant_options", key: "size") {
              value
            }
          }
        }
      }
    }
  `
  return { query, variables }
}

export const queryChildBundleItemsBySKU = (skus) => {
  if (!skus || !(skus instanceof Array)) {
    throw new Error(`Must provide skus to queryChildBundleItemsBySKU; provided: ${typeof skus === 'object' ? JSON.stringify(skus) : skus} `)
  }

  const variables = buildSkuQueryVariables(skus)
  const query = `
    query productsBySku($query: String!, $first: Int) {
      products(first: $first, query: $query) {
        edges {
          node {
            id
            title
            handle
            variants(first: 10) {
              edges {
                node {
                  sku
                  availableForSale
                  quantityAvailable
                  onHandStock: metafield(namespace: "custom", key: "onhandstock") {
                    value
                  }
                  futureStock: metafield(namespace: "custom", key: "futurestock") {
                    value
                  }
                  preOrderDate: metafield(namespace: "custom", key: "earliestpodate") {
                    value
                  }
                  brisbaneStockJson: metafield(namespace: "custom", key: "brisbane-stock-json") {
                    value
                  }
                  melbourneStockJson: metafield(namespace: "custom", key: "melbourne-stock-json") {
                    value
                  }
                }
              }
            }
          }
        }
      }
    }
  `
  return { query, variables }
}
